import {
  allowedChildTypes,
  type BuildMaturity,
  type CurrentUser,
  type NodeTree,
} from '@capsio/contracts';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, messageOf } from '../api-client.js';
import { useDismissOnOutside } from '../components/dismiss.js';
import { ProposedCardDetail } from './ProposedCardDetail.js';
import { ExecPage } from './ExecPage.js';
import { IngestionPage } from './IngestionPage.js';
import { AtcDetail } from './AtcDetail.js';
import { Breadcrumbs } from './Breadcrumbs.js';
import { ChangePasswordDialog } from './ChangePasswordDialog.js';
import { ATC_CLASS_LABEL } from '@capsio/contracts';
import {
  type AtcFilter,
  canHoldAssetsOf,
  crumbsFor,
  indexTree,
  resolveStationId,
  ROOT_ID,
  typeOf,
} from './node-tree-path.js';
import { NodeTree as NodeTreeView } from './NodeTree.js';
import { TreeLegend } from '../prototype/TreeLegend.js';
import { SearchDialog } from './SearchDialog.js';
import { SettingsDialog } from './SettingsDialog.js';
import { SideResizer } from './SideResizer.js';
import { clampSideWidth, readSideWidth } from './side-width.js';
import { UnitDetail } from './UnitDetail.js';
import { AssetLedger } from '../prototype/AssetLedger.js';
import { StatsPage } from '../prototype/StatsPage.js';
import { StubView, STUB_KEYS, type StubKey } from '../prototype/StubView.js';
import { TopTabs, tabKeyOf, type TabKey } from '../prototype/TopTabs.js';

/** 还没拉到数据时用它，省掉「index 可能为 null」在下面每处的判空。 */
const EMPTY_TREE: NodeTree = { types: [], nodes: [] };

/*
 * ATC：带勾的清单板。
 *
 * 原来画的是「一份带折角的文件」，那说的是「一个文档」。而 ATC 是**照着做完一件事的清单**——
 * 有步骤、要逐条确认。清单板加一个勾，说的才是这件事。
 */
/*
 * 搜索：放大镜。
 *
 * 跟侧栏开关那个面板图标并排，所以线宽跟它一样（1.3）——两个图标粗细不一致，
 * 那一行看起来就是歪的。
 */
const GLASS = (
  <svg width="15" height="15" viewBox="0 0 15 15" aria-hidden="true">
    <g fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round">
      <circle cx="6.6" cy="6.6" r="4.4" />
      <path d="M10 10 L13.2 13.2" />
    </g>
  </svg>
);

const GEAR = (
  <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
    <g fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round">
      <circle cx="7" cy="7" r="2.2" />
      <path d="M7 1.2 v1.4 M7 11.4 v1.4 M1.2 7 h1.4 M11.4 7 h1.4 M3 3 l1 1 M10 10 l1 1 M11 3 l-1 1 M4 10 l-1 1" />
    </g>
  </svg>
);

/**
 * 侧栏开关的图标：一个画着侧栏的面板。
 *
 * 收起和展开共用同一个图标——它表达的是「这块面板」这个对象，
 * 而不是某个方向的动作；方向由 title 说清楚。箭头反而会和树里节点自己的展开箭头混淆。
 */
const PANEL_ICON = (
  <svg width="15" height="15" viewBox="0 0 15 15" aria-hidden="true">
    <rect
      x="1.6"
      y="2.6"
      width="11.8"
      height="9.8"
      rx="2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
    />
    <path d="M6 2.6 V12.4" stroke="currentColor" strokeWidth="1.3" />
    <rect x="2.7" y="3.7" width="2.5" height="7.6" fill="currentColor" opacity="0.32" />
  </svg>
);

function AccountMenu({
  user,
  organizationCode,
  onClose,
  onChangePassword,
  onSignedOut,
}: {
  user: CurrentUser;
  organizationCode: string;
  onClose: () => void;
  onChangePassword: () => void;
  onSignedOut: () => void;
}): ReactNode {
  const box = useRef<HTMLDivElement | null>(null);
  useDismissOnOutside(box, onClose);

  return (
    <div className="account-menu" ref={box} role="menu">
      <div className="who-block">
        <span className="who-mail">{user.email}</span>
        <div className="who-tags">
          <span className="role">{user.role === 'admin' ? '管理员' : '成员'}</span>
          {/* 企业标识从顶栏挪到这儿：它回答的是「我在哪一套部署里」，属于账号信息。 */}
          <span className="orgcode">{organizationCode}</span>
        </div>
      </div>
      <button
        className="menu-item"
        type="button"
        role="menuitem"
        onClick={() => {
          onClose();
          onChangePassword();
        }}
      >
        改密码
      </button>
      <button
        className="menu-item"
        type="button"
        role="menuitem"
        onClick={() => {
          void api.logout().then(onSignedOut, onSignedOut);
        }}
      >
        退出
      </button>
    </div>
  );
}

export function AppShell({
  user,
  organizationName,
  organizationCode,
  onUserChanged,
  onOrganizationChanged,
  onSignedOut,
}: {
  user: CurrentUser;
  organizationName: string;
  organizationCode: string;
  onUserChanged: (user: CurrentUser) => void;
  /** 企业信息改过了。名称是树根、也在面包屑上；标识在账号浮层里，都要跟着变。 */
  onOrganizationChanged: () => void;
  onSignedOut: () => void;
}): ReactNode {
  const [collapsed, setCollapsed] = useState(false);
  /**
   * 侧栏宽度。初值从 localStorage 读，读不到用默认。
   *
   * 拖动期间 SideResizer 直接改 --side-w，不经过这个 state——每次 pointermove
   * 都重渲染整棵树会发涩。松手才提交到这儿，让 React 和 DOM 在静止时一致。
   */
  const [sideWidth, setSideWidth] = useState(() => readSideWidth(window.innerWidth));
  const shellRef = useRef<HTMLDivElement | null>(null);
  /**
   * 侧栏两页：组织架构，和全公司的 ATC。
   *
   * 不放进地址栏——它是看的方式，不是看的东西。真正需要刷新不丢的是选中了哪个节点，
   * 那个已经在 `?node=` 里了。
   */
  /** 树筛选器：全部 / 运营 ATC / 建设 ATC（原型 v0.22 第 331–333 行）。 */
  /**
   * 记住的站点：最后一次选中的组织节点。
   *
   * 点 ATC 不动它——不然点一份挂在下级岗位上的标准，整列会从「整个部门的」缩成
   * 「一个岗位的」，正在挑的兄弟几份就没了。null 表示还没选过，那时从选中项推算。
   *
   * 不进地址栏：它是「从哪儿看」，刷新之后由选中项推出一个合理的落点就够了。
   */
  const [pinnedStation, setPinnedStation] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * 待建卡的深链（`?pool=<code>`）。
   *
   * 三页签撤掉之后**没有侧栏入口**了——待建卡是 `atc` 表里 `proposed` 态的行，
   * 树上一视同仁地列着它们。留这两个参数只为让既有链接不失效
   * （有人把某张待建卡的地址发给过同事）。
   */
  const atPool = searchParams.get('pool') !== null;

  /** 树筛选器：全部 / 运营 / 建设。组件内状态——它不改变「在看哪个对象」。 */
  const [atcFilter, setAtcFilter] = useState<AtcFilter>('all');
  const poolCode = searchParams.get('pool');
  const [tree, setTree] = useState<NodeTree | null>(null);
  const [treeError, setTreeError] = useState<string | null>(null);
  /* 建设成熟度（左栏行尾的「N 卡 · 百分比」）：派生视图，跟树同一次拉。 */
  const [maturity, setMaturity] = useState<BuildMaturity | null>(null);

  /** 整棵树一次拉完。进来拉一次，从设置回来再拉一次——架构和资产都在设置里改。 */
  function reload(): void {
    void api.tree().then(
      (data) => {
        setTree(data);
        setTreeError(null);
      },
      (cause: unknown) => setTreeError(messageOf(cause)),
    );
    // 成熟度拉不到不挡树：行尾退回只报卡数（NodeTree 里有这条退路）
    void api.orgBuildMaturity().then(
      (data) => setMaturity(data),
      () => undefined,
    );
  }

  useEffect(reload, []);

  /**
   * 台账「修改」的落点（C-190 批三 A8）：跳卡页并**直接进入编辑态**。
   * 所见即所得——改定义就在定义呈现的地方改。台账那个两字段的表单撤了：
   * 同一个字段两个入口，且其中一个只能改五分之一。
   * 停用/启用不走这里——那是状态动作，留在台账（批量场景不该跳来跳去）。
   */
  function openCardForEdit(id: string): void {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      next.delete('new');
      next.delete('view');
      next.delete('atc');
      // 回到树所在的页签——带着 tab=setup 跳过去会停在台账页，卡页根本不出来
      next.delete('tab');
      next.set('node', id);
      next.set('edit', '1');
      return next;
    });
  }

  /*
   * ⌘K / Ctrl+K 打开搜索。
   *
   * 挂在 window 上而不是某个元素上：这是全局入口，人在页面任何地方都该按得动。
   * preventDefault 是必须的——Firefox 里 Ctrl+K 是「跳到搜索栏」，不拦就被浏览器抢走。
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key.toLowerCase() !== 'k' || !(event.metaKey || event.ctrlKey)) return;
      event.preventDefault();
      setSearchOpen(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const canManage = user.role === 'admin';
  const index = useMemo(() => indexTree(tree ?? EMPTY_TREE), [tree]);


  /**
   * 选中哪个节点由地址栏说——`?node=<id>`，没有这个参数就是企业（根）。
   *
   * 放在 URL 里而不是组件状态里：刷新不丢、能收藏、能把「看这个」发给同事。
   *
   * 「什么都没选」这个状态不存在：它要求面包屑和渲染区各自准备一套空文案，
   * 而用户在那个状态下能做的唯一一件事就是离开它。
   *
   * 树还没拉到时先信地址栏；拉到之后发现那个 id 不在树上（比如在设置里被删了），
   * 就当成根来渲染。不去改写地址栏——那要么写在 effect 里，要么得靠 ref 拿最新值，
   * 而下一次点击自然会把参数换掉，留着的那个死参数只是不好看，不影响任何行为。
   */
  /**
   * 原始记录池：一个**平级的去处**，不是树上的节点，也不属于任何一张卡。
   *
   * 放在地址栏（`?view=ingest`）而不是组件状态里，理由与 `?node=` 同：
   * 刷新不丢、能收藏、能把「看这个」发给同事。
   */
  const atIngest = searchParams.get('view') === 'ingest';
  /** ATC 执行页。与记录池同为平级去处；`?atc=` 空表示还没挑卡。 */
  const atExec = searchParams.get('view') === 'exec';

  /**
   * 顶部九页签当前在哪一页（原型 v0.22 `#topTabs`，C-159 三）。
   *
   * 同样放地址栏，理由与 `?node=` / `?view=` 一致。认不出的值当总览——
   * 不报错也不显示空白页（`tabKeyOf`）。
   */
  const tab: TabKey = tabKeyOf(searchParams.get('tab'));
  const execCode = searchParams.get('atc');

  const urlNode = searchParams.get('node');
  /*
   * 路径此刻算不出来：树还没回来，而地址栏里指着一个具体节点。
   *
   * **两件事必须分开**——「树没到」与「这个节点真的不存在」。
   * 后者是合法情形（收藏了一条旧链接、节点已归档），那时回落到根是对的；
   * 前者只是还没到，回落到根就是**在还不知道答案时给了一个答案**。
   * `tree === null` 恰好只在前者成立，故用它判，不用 `index.byId.size === 0`
   * ——空树也可能是真的空（新实例什么都还没建），那时不该显示「读取中」。
   */
  const pathUnknown = tree === null && urlNode !== null;

  const onTree = (id: string): boolean => id === ROOT_ID || tree === null || index.byId.has(id);

  /**
   * 正在新建一份 ATC，值是它建在哪个节点下面（`?new=<id>`，企业本身是 ROOT_ID）。
   *
   * 也放地址栏：这一页是个正经的页面而不是浮层，刷新不该把人踢回去。填的内容留不住
   * （草稿在内存里），但「我在给设备部建一份」这件事留得住。
   */
  const draftParent = searchParams.get('new');
  /*
   * 树没拉到之前不进新建页：那一页要算编码、要知道能建哪些类型，两件事都得有整棵树。
   * 先渲染一下空的、等数据到了再变成新建页，中间那一帧是坏的。
   */
  const draftTarget =
    canManage && draftParent !== null && tree !== null && onTree(draftParent) ? draftParent : null;
  const draftParentNode =
    draftTarget === null || draftTarget === ROOT_ID ? null : (index.byId.get(draftTarget) ?? null);

  /**
   * 这个位置允许建哪些资产类型。新建页用它决定类型那一格是一行字还是一个下拉。
   *
   * 只留资产那一类：组织架构不在这儿改，它在设置里。
   */
  const assetOptions = allowedChildTypes(
    tree?.types ?? [],
    draftParentNode === null ? null : (typeOf(index, draftParentNode) ?? null),
  ).filter((option) => option.kind === 'ASSET');

  /*
   * 一种都建不了就不进新建页——手敲一个 `?new=<某份 ATC 的 id>` 就是这种情况
   * （ATC 是叶子）。当成参数无效忽略掉，跟无效的 `?node=` 一个处理法，
   * 而不是渲染一个类型是空的、按了必被后端拒的表单。
   */
  const drafting = draftTarget !== null && assetOptions.length > 0;

  /*
   * 草稿时选中的是它的**父节点**：面包屑、树上的高亮、侧栏那一列都因此停在
   * 「建在哪儿」上，不用为草稿再各写一套。最后那一段「新建 ATC」由面包屑单独接。
   */
  const selectedId = drafting
    ? draftTarget
    : urlNode !== null && onTree(urlNode)
      ? urlNode
      : ROOT_ID;

  function openExec(code: string): void {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      next.set('view', 'exec');
      if (code === '') next.delete('atc');
      else next.set('atc', code);
      next.delete('new');
      return next;
    });
  }

  function openIngest(): void {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      next.set('view', 'ingest');
      next.delete('new');
      return next;
    });
  }

  function openTab(next: TabKey): void {
    setSearchParams((previous) => {
      const params = new URLSearchParams(previous);
      if (next === 'home') params.delete('tab');
      else params.set('tab', next);
      /*
       * 换页签就离开记录池与执行页：那两页是**平级去处**，不在页签体系里，
       * 留着 `?view=` 的话页签看起来切了、渲染区还是原来那一页。
       */
      params.delete('view');
      params.delete('new');
      return params;
    });
  }

  /** 进新建页。`?new=` 取代 `?node=`——选中项由前者推出来，两个都留会有两份真相。 */
  function startCreate(parentId: string): void {
    setPinnedStation(parentId);
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      next.delete('node');
      next.set('new', parentId);
      return next;
    });
  }

  function selectNode(id: string): void {
    /*
     * 选中组织节点才移动站点。选中 ATC 只换渲染区，站点留在原地——
     * 这样一边看详情一边在同一列里换着挑才成立。
     */
    const node = id === ROOT_ID ? null : (index.byId.get(id) ?? null);
    if (node === null || typeOf(index, node)?.kind !== 'ASSET') setPinnedStation(id);

    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        // 离开新建页。留着这个参数的话，选中项还会被它压着。
        next.delete('new');
        // 也离开记录池与执行页——点树上的东西就是「回到树」。
        next.delete('view');
        next.delete('atc');
        /*
         * 重置顶部页签到总览（C-210 A2）。
         *
         * 点树上任何节点 = 回到这个节点的总览主页。不留上一个节点的
         * tab 状态——否则「部门 A · 日常审计」→ 点岗位 B → 停在
         * 「岗位 B · 日常审计」，而日常审计那个页签是占位的，人进去了也不知道自己在看什么。
         *
         * 边界：同节点内点顶部 tab 切换走 openTab，不经过这里，不受影响。
         * 浏览器返回能回到此前的 node+tab 组合——replace:false 保证正常入栈。
         */
        next.delete('tab');
        // 台账「修改」跳转过来的编辑态也一并离开——点树就是回到总览。
        next.delete('edit');
        // 根不留参数，地址栏干净些。
        if (id === ROOT_ID) next.delete('node');
        else next.set('node', id);
        return next;
      },
      // 默认入历史：浏览器的返回键因此能退回上一个节点，这符合「点树就是在导航」。
      { replace: false },
    );
  }

  const crumbs = crumbsFor(index, selectedId, organizationName);

  /**
   * 选中的是不是一个资产。是就渲染资产自己的详情页。
   *
   * 判断走 kind 而不是 `typeCode === 'ATC'`：以后加第二种资产时，这儿不该跟着改。
   */
  const selectedNode = selectedId === ROOT_ID ? null : (index.byId.get(selectedId) ?? null);
  const selectedAsset =
    selectedNode !== null && typeOf(index, selectedNode)?.kind === 'ASSET' ? selectedNode : null;

  /**
   * 当前这一站：侧栏 ATC 页和渲染区列的就是它往下整支的东西，树上高亮的也是它。
   *
   * 资产不在侧栏里，所以选中一份 ATC 时高亮的是它所在的那一站——不然整棵树
   * 没有一行是亮的，人就不知道自己站在哪儿。
   */
  const stationId = resolveStationId(index, pinnedStation, selectedId);
  const highlightId = stationId;

  /**
   * 路径条末尾给不给那个「+」。给的话建到**正在看的这个组织节点**下面——
   * 它就挨在那一段后面，所以不用再解释建到哪儿去。
   *
   * 四种情况不给：没权限；已经在新建页上了；正在看一份 ATC（一份标准里面加不了标准）；
   * 类型规则说这儿挂不了资产——一个注定被后端拒掉的按钮，比没有这个按钮更难解释。
   */
  const canCreateHere =
    canManage && !drafting && selectedAsset === null && canHoldAssetsOf(index, selectedId);

  return (
    <div
      className="app"
      ref={shellRef}
      style={{ '--side-w': `${sideWidth}px` } as React.CSSProperties}
    >
      {/*
        `pv22` 挂在 `<aside>` 上（提供 CSS 变量 `--hd` / `--rule2` 等），
        而原型规则形如 `.side .flt` / `.side .legend`——**`.side` 必须是 `.pv22` 的后代，
        不能与它同元素**（`.pv22 .side` 选不中「两个类在同一个元素上」的情形）。

        故内层加一个 `className="side"` 且 `display: contents` 的壳：
        **它只提供选择器祖先，不参与布局**——侧栏是 flex 列，插一个正常 div
        会把树的 `flex:1` 隔断（第一版就是这么塌的）。

        **文档级 reset 已在生成时整条丢弃**（`ops/prototype/scope-css.py` 的 `DROP_SELECTORS`）
        ——第一版把 `html,body{height:100%}` 映射进来，包装 div 拿到 100% 高度把树挤没了；
        `*{margin:0}` 则清掉了被包住的应用元素的间距。
        **reset 属于「整个页面」这个上下文，它没有子树版本。**
      */}
      {!collapsed && (
        <aside className="sidebar pv22">
          <div className="side-head">
            {/* 顶部只放产品名。企业名是树的根节点，也在面包屑里，不用在这儿再说一遍。 */}
            <span className="wordmark">Capsio Hub</span>
            <button
              className="iconbtn"
              type="button"
              onClick={() => setCollapsed(true)}
              title="收起侧边栏"
              aria-label="收起侧边栏"
            >
              {PANEL_ICON}
            </button>
            {/* 收起时整条侧栏都没了，这个入口跟着没；那时候还有 ⌘K。 */}
            <button
              className="iconbtn"
              type="button"
              onClick={() => setSearchOpen(true)}
              title="搜索（⌘K）"
              aria-label="搜索"
            >
              {GLASS}
            </button>
          </div>

          {/*
            ## 三页签已撤，左栏只有一棵树（C-161 A3）

            此前是「组织 / ATC / 资产清单」三个页签，切的是「侧栏列什么」。
            现在树本身就是四层——企业 → 部门 → 岗位 → ATC——**卡就在树上**，
            不需要另一页去列它们。左侧是资产，这是界面第一原则。

            待建卡（`proposed` 态）也在树上：它们是 `atc` 表里的行，
            树的投影一视同仁地列出来。`?pool=` 这个深链仍然可用，只是不再有页签入口。
          */}
          {/*
            ## 「原始记录池」「ATC 执行页」两个入口已移出资产树面板（C-161 A3）

            **左侧是资产。** 那两页不是资产，是另外两个去处：

            | 页 | 现在从哪进 |
            |---|---|
            | 原始记录池 | 顶部页签「数据与接入」 |
            | ATC 执行页 | 卡页的「运行记录」分区 |

            路由 `?view=ingest` / `?view=exec` 都还在，深链不失效——
            撤掉的是**侧栏里那两个按钮**，不是那两页。
          */}
          {treeError !== null && <p className="msg tree-error">{treeError}</p>}

          {/*
           * **`.pv22` 只包真正搬入的原型 markup，不包应用自己的树。**
           *
           * 第一版把整棵树包进去了，结果 `.pv22 *{margin:0;padding:0}` 这条 reset
           * 把应用自己的树样式**全部清掉**——树用的是 `.node-row`／`.node-btn`
           * 这套应用 markup，原型 CSS 不认它，于是只剩 reset 生效、行距全塌。
           *
           * 边界要划在「这一块的 markup 是不是原型的」上。
           */}
          <div className="side" style={{ display: 'contents' }}>
            {/*
              运营 / 建设筛选器。位置与形态照原型（`.flt`，侧栏树上方）。

              **接的是 `atc.cls`**，不是 `owner_scope`——后者是「客户 / 我方」，
              是另一个轴。上一卡因为库里没有 `cls` 把这块整块撤了，现在字段落库，装回来。
            */}
            <div className="flt" role="group" aria-label="运营 / 建设筛选">
              {(
                [
                  ['all', '全部'],
                  ['operational', ATC_CLASS_LABEL.operational],
                  ['construction', ATC_CLASS_LABEL.construction],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className={atcFilter === key ? 'on' : undefined}
                  onClick={() => setAtcFilter(key)}
                >
                  {label}
                </button>
              ))}
            </div>

            <NodeTreeView
              index={index}
              rootName={organizationName}
              loading={tree === null}
              highlightId={highlightId}
              onSelect={selectNode}
              canCreate={canManage}
              onCreate={startCreate}
              filter={atcFilter}
              revealId={selectedId}
              maturity={maturity}
            />

            <TreeLegend />
          </div>

          <div className="side-bottom">
            <button
              className="account"
              type="button"
              aria-haspopup="menu"
              aria-expanded={accountOpen}
              onClick={() => setAccountOpen(!accountOpen)}
            >
              <span className="avatar">{user.name.slice(0, 1)}</span>
              <span className="acct-name">{user.name}</span>
            </button>

            {canManage && (
              <button
                className="iconbtn"
                type="button"
                onClick={() => setSettingsOpen(true)}
                title="设置"
                aria-label="设置"
              >
                {GEAR}
              </button>
            )}

            {accountOpen && (
              <AccountMenu
                user={user}
                organizationCode={organizationCode}
                      onClose={() => setAccountOpen(false)}
                onChangePassword={() => setChangingPassword(true)}
                onSignedOut={onSignedOut}
              />
            )}
          </div>
        </aside>
      )}

      {/* 收起的时候没有边界可拖。 */}
      {!collapsed && (
        <SideResizer
          shellRef={shellRef}
          width={sideWidth}
          onCommit={(px) => setSideWidth(clampSideWidth(px, window.innerWidth))}
        />
      )}

      <section className="main-col">
        <div className="crumb-bar">
          {collapsed && (
            <button
              className="iconbtn"
              type="button"
              onClick={() => setCollapsed(false)}
              title="展开侧边栏"
              aria-label="展开侧边栏"
            >
              {PANEL_ICON}
            </button>
          )}

          {/*
            路径条。每一段的折角一直都有，不跟侧栏的收起状态挂钩——侧栏展开时它也有用，
            尤其是要跳到一份 ATC：ATC 不在树上，只有这儿能钻进去。
          */}
          {atIngest || atExec ? (
            /* 这两页不在树上，没有路径可走——给一段纯文字，不给一条假的面包屑。 */
            <span className="crumb-cur">{atIngest ? '原始记录池' : 'ATC 执行页'}</span>
          ) : pathUnknown ? (
            /*
             * 树还没到、而地址栏里指着一个节点：**此刻这条路径是算不出来的**。
             *
             * 不渲染面包屑，给一段占位。**宁可空一下，也不要先显示一个错的再改过来**：
             * `pathTo` 在 `index` 为空时会把任何 id 判成「不存在」而回落到根
             * （`node-tree-path.ts:85`），于是首帧显示的是企业根——
             * 一个**看起来完全正常**的错误答案，200 毫秒后自己变了。
             *
             * 实测过：首 200ms 是企业根，+600ms 起才对，三次复跑一致。
             * 这种一闪而过的错值最难查——它不报错，截图也未必抓得到，
             * 但人眼会读到「我选的是岗位，它显示企业」。
             */
            <span className="crumb-cur crumb-pending" aria-busy="true">
              读取路径……
            </span>
          ) : (
            <Breadcrumbs
              crumbs={crumbs}
              onSelect={selectNode}
              // 草稿还没有 id，点不了，所以它是一段纯文字而不是又一个 crumb。
              tail={drafting ? '新建 ATC' : undefined}
              onCreate={canCreateHere ? () => startCreate(selectedId) : undefined}
            />
          )}
        </div>

        {/*
          九页签。原型里它在面包屑**下面**（350 行紧跟 `#crumb`），照搬这个顺序。

          记录池与执行页是**平级去处**，不在页签体系里，故那两页不显示页签条——
          显示的话，页签会亮着「总览」而渲染区是另一页，两处对不上。
        */}
        {!atIngest && !atExec && (
          <TopTabs current={tab} showAtc={selectedAsset !== null} onPick={openTab} />
        )}

        <div className="render-body">
          {/*
            新建 ATC 走的是**同一个** AtcDetail：一份还没落库的草稿。
            以前它是个弹窗（名称 + 编码两个字段），跟这一页是两套东西——
            而「新建一份 ATC」要填的正是这一页显示的内容。
          */}
          {atPool && poolCode !== null ? (
            /*
              待建卡走自己的详情组件，不复用 AtcDetail：后者的对象是树上的节点，
              而待建卡是 app.atc 里的一行。硬塞进同一个组件，就得在里面到处判断
              「这次是哪一种」，而两种的字段几乎没有重合。
            */
            <ProposedCardDetail key={poolCode} code={poolCode} />
          ) : atIngest ? (
            <IngestionPage />
          ) : atExec ? (
            <ExecPage
              code={execCode === null || execCode === '' ? null : execCode}
              onPick={openExec}
            />
          ) : STUB_KEYS.includes(tab as StubKey) ? (
            /*
              六个结构占位页签。**原型里它们本来就是占位**（同一个 `stubHTML`），
              搬入的是那份信息架构：哪一页管什么、分几块。

              「数据与接入」多给一个真入口——原始记录池那一页是真的、链路已通，
              而它正是这一页要管的东西。其余五页没有已通的链路，故不编入口。
            */
            <div className="pv22 pv22-stack">
              <StubView which={tab as StubKey} />
              {tab === 'data' && (
                <div className="card">
                  <h3>
                    原始记录池 <span className="sub">这一页是真的 · 不是占位</span>
                  </h3>
                  <p className="note">
                    四类载体的原始记录都进这一个池子：只追加、不改写。
                  </p>
                  <button type="button" className="btns" onClick={openIngest}>
                    去原始记录池
                  </button>
                </div>
              )}
            </div>
          ) : tab === 'stats' ? (
            /*
              运营统计（C-168）。**随左栏节点变**：企业节点看企业级表，
              部门节点看部门级，岗位节点看岗位级。

              用的是**同一个 `stationId`**，与「总览」那一套一致——
              两处各算一遍「现在站在哪一级」，迟早会在同一屏上给出两个层级。
            */
            <StatsPage
              nodeId={stationId === ROOT_ID ? null : stationId}
              rootName={organizationName}
            />
          ) : tab === 'setup' ? (
            /*
              ATC 设置 = 资产台账四张表。**表格自己拉领域接口**
              （`/api/org/*`），不从这里的树投影取数——所以这儿不传 `index`。
              树只负责「选中了谁」（`selectedId`）与「点了跳哪儿」（`selectNode`）。
            */
            /*
              筛选状态**与左栏共用一份**（C-181-补1 补-B 联动裁定）：
              筛选是「换一张图看」，图只有一张——左栏筛「建设」，
              台账四张表与统计卡也在同一张图上。两处各持一份状态，
              就会出现左栏在建设视图、台账在全部视图的错位。
            */
            <AssetLedger
              selectedId={selectedId}
              onSelect={selectNode}
              clsFilter={atcFilter}
              onClsFilterChange={setAtcFilter}
              onChanged={reload}
              onEditAtc={openCardForEdit}
            />
          ) : drafting ? (
            <AtcDetail
              /*
                换了对象就重挂一次：name / tab / error 都是组件内部状态，
                React 在同一个位置渲染同一种组件是会留着它们的——
                改到一半跳去另一份 ATC，输入框里就还是上一份的名字。
              */
              key={`draft:${selectedId}`}
              index={index}
              subject={{
                kind: 'draft',
                parentId: selectedId === ROOT_ID ? null : selectedId,
                options: assetOptions,
              }}
              rootName={organizationName}
              canManage={canManage}
              onChanged={reload}
              onCreated={(saved) => {
                // 建好就跳到它自己那一页，接着填内容。不入历史：返回键该回到建之前那儿。
                setSearchParams(
                  (previous) => {
                    const next = new URLSearchParams(previous);
                    next.delete('new');
                    next.set('node', saved.id);
                    return next;
                  },
                  { replace: true },
                );
                reload();
              }}
              onCancelDraft={() => selectNode(selectedId)}
              onSelect={selectNode}
            />
          ) : selectedAsset === null ? (
            /* 组织节点看它自己是什么。它的 ATC 那一列在侧栏 ATC 页上。 */
            <UnitDetail
              index={index}
              node={selectedNode}
              canCreate={canManage}
              onSelect={selectNode}
            />
          ) : (
            <AtcDetail
              key={selectedAsset.id}
              index={index}
              subject={{ kind: 'saved', node: selectedAsset }}
              rootName={organizationName}
              canManage={canManage}
              onChanged={reload}
              // 已存在的那一份不会变成草稿，这两个回调用不上。
              onCreated={() => undefined}
              onCancelDraft={() => undefined}
              onSelect={selectNode}
              startEditing={searchParams.get('edit') === '1'}
            />
          )}
        </div>
      </section>

      {searchOpen && (
        <SearchDialog
          index={index}
          rootName={organizationName}
          organizationCode={organizationCode}
          onPick={(id) => {
            setSearchOpen(false);
            // 搜到的可能是一份 ATC，也可能是个组织节点——selectNode 两种都认
            selectNode(id);
          }}
          onClose={() => setSearchOpen(false)}
        />
      )}

      {settingsOpen && canManage && (
        <SettingsDialog
          me={user}
          organizationName={organizationName}
          onOrganizationChanged={onOrganizationChanged}
          onClose={() => {
            setSettingsOpen(false);
            // 架构和资产都在设置里改，关掉就得重新拉一遍，否则侧栏还是旧的。
            reload();
          }}
        />
      )}

      {/* 强制那条优先：还没改掉别人给的密码时，不给他别的事做。 */}
      {(user.mustChangePassword || changingPassword) && (
        <ChangePasswordDialog
          forced={user.mustChangePassword}
          onDone={onUserChanged}
          onClose={() => setChangingPassword(false)}
        />
      )}
    </div>
  );
}
